import { Link, matchPath, useLocation } from 'react-router-dom';
import * as React from 'react';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { IIconProps } from '../Helpers/Common';
import { ILink } from './Navigation';
import NavigationLinks from './NavigationLinks';
import If from '../If/If';

interface INavigationLinkProps extends IIconProps {
	path: string;
	className?: string;
	label: React.ReactNode;
	isParent?: boolean;
	subLinks?: ILink[];
	subLinksFilter?: (link: ILink) => boolean;
	onClick?: (event: React.MouseEvent<HTMLLIElement, MouseEvent>) => void;
	isActive?: boolean;
	isDisabled?: boolean;
	useATag?: boolean;
	customComponent?: React.ReactNode;
	innerTagClass?: string;
	showRedDot?: boolean;
}

const NavigationLink = observer((props: INavigationLinkProps) => {
	const location = useLocation();
	const liRef = React.useRef<HTMLLIElement | null>(null);
	const [subLinksExpanded, setSubLinksExpanded] = React.useState(false);

	React.useEffect(() => {
		const handleDocumentClick: EventListener = e => {
			// Need to assert the type here https://github.com/Microsoft/TypeScript/issues/15394
			const target = e.target as Node;

			if (
				subLinksExpanded &&
				!!liRef.current &&
				(!liRef.current.contains(target) ||
					(!!liRef.current.lastElementChild && liRef.current.lastElementChild.contains(target)))
			) {
				setSubLinksExpanded(!subLinksExpanded);
			}
		};

		document.addEventListener('click', handleDocumentClick);

		return () => {
			document.removeEventListener('click', handleDocumentClick);
		};
	}, [subLinksExpanded]);

	const icon = React.useMemo(() => {
		if (props.icon) {
			return `icon-${props.icon}`;
		}
		return undefined;
	}, [props.icon]);

	const iconPos = React.useMemo(() => {
		if (icon) {
			return props.iconPos ?? 'icon-left';
		}
		return undefined;
	}, [icon, props.iconPos]);

	const isActive = (path: string) => {
		if (matchPath({ path, end: true }, location.pathname)) return true;
		// Also match sub-routes (e.g. /account-details matches /account-details/account)
		// but skip root path to avoid matching everything
		if (path.length > 1) {
			return location.pathname === path || location.pathname.includes(`${path}/`);
		}
		return false;
	};

	const onClick = (event: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
		if (props.isParent) {
			setSubLinksExpanded(!subLinksExpanded);
		}
		if (props.onClick) {
			props.onClick(event);
		}
	};

	const {
		className,
		customComponent,
		path,
		label,
		isActive: isActiveProp,
		isDisabled,
		isParent,
		subLinks,
		subLinksFilter,
		useATag,
		innerTagClass,
		showRedDot,
	} = props;

	let subLinksNode = null;
	if (isParent && !!subLinks) {
		const ulClazz = classNames('nav__sublinks', subLinksExpanded ? 'nav__sublinks--visible' : '');
		subLinksNode = <NavigationLinks className={ulClazz} links={subLinks || []} filter={subLinksFilter} />;
	}

	const isActiveState = isActiveProp ?? isActive(path);

	const textNode = !!icon ? <span>{label}</span> : label;

	let body: React.ReactNode;
	if (isParent || isDisabled) {
		body = (
			<>
				<button
					className={classNames(innerTagClass, icon, iconPos, 'nav__link')}
					aria-label={typeof label === 'string' ? label : undefined}
				>
					{textNode}
				</button>
				<If condition={showRedDot}>
					<span className="red-dot" />
				</If>
			</>
		);
	} else if (customComponent !== undefined) {
		body = customComponent;
	} else if (useATag) {
		body = (
			<>
				<a
					href={path}
					rel="noopener noreferrer"
					className={classNames(innerTagClass, icon, iconPos, 'nav__link')}
					target="_blank"
					aria-label={typeof label === 'string' ? label : undefined}
				>
					{textNode}
				</a>
				<If condition={showRedDot}>
					<span className="red-dot" />
				</If>
			</>
		);
	} else {
		body = (
			<>
				<Link
					to={path}
					className={classNames(innerTagClass, icon, iconPos, 'nav__link')}
					aria-label={typeof label === 'string' ? label : undefined}
				>
					{textNode}
				</Link>
				<If condition={showRedDot}>
					<span className="red-dot" />
				</If>
			</>
		);
	}

	return (
		<li
			ref={liRef}
			className={classNames(
				subLinksExpanded && isParent ? 'nav__parent-link--active' : undefined,
				isActiveState && !isParent ? 'active' : undefined,
				className,
			)}
			key={path}
			onClick={onClick}
		>
			{body}
			{subLinksNode}
		</li>
	);
});

export default NavigationLink;
